// HTML für E-Mails im Browser: bereinigen (gleiche Teilmenge wie der Server), Bilder des CRM zwischen den zwei Schreibweisen
// umsetzen und Signatur und Zitat im Text finden.
//
// Gespeichert und gesendet wird `<img src="cid:<Datei-ID>">` (ID in `kommunikation_dateien`). Im Browser zeigt ein Bild nur
// `/api/mail/dateien/<ID>`; der Editor rechnet beim Laden `cid:` -> Adresse und beim Melden Adresse -> `cid:`.
// Bilder aus dem Netz (andere Adressen) lassen wir nie zu: kein Nachladen von Pixeln beim Lesen fremder Mails.

const DATEI_PFAD = "/api/mail/dateien/";
const ID = "file_crm_[A-Za-z0-9]+";
const CID_ZU_URL = new RegExp(`(<img\\b[^>]*?\\bsrc=")cid:(${ID})(")`, "gi");
const URL_ZU_CID = new RegExp(`(<img\\b[^>]*?\\bsrc=")(?:https?://[^/"]+)?${DATEI_PFAD}(${ID})(?:\\?[^"]*)?(")`, "gi");

export function cidZuUrl(html: string): string {
  return html.replace(CID_ZU_URL, `$1${DATEI_PFAD}$2$3`);
}

export function urlZuCid(html: string): string {
  return html.replace(URL_ZU_CID, "$1cid:$2$3");
}

const ERLAUBT = new Set([
  "P", "BR", "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "DEL", "UL", "OL", "LI", "A", "BLOCKQUOTE", "H1", "H2", "H3", "H4",
  "SPAN", "DIV", "CODE", "PRE", "HR", "TABLE", "THEAD", "TBODY", "TR", "TD", "TH", "SUB", "SUP", "MARK", "IMG",
]);
const ENTFERNEN = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "NOSCRIPT", "SVG", "MATH", "FORM", "INPUT", "BUTTON", "TEXTAREA",
  "SELECT", "VIDEO", "AUDIO", "LINK", "META", "HEAD", "TITLE", "BASE", "FRAME",
]);
const KLASSEN: Record<string, string[]> = { BLOCKQUOTE: ["crm-zitat"], DIV: ["crm-signatur", "crm-zitat"] };
const FARBE = /^\s*color\s*:\s*(#[0-9a-f]{3}|#[0-9a-f]{6}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))\s*;?\s*$/i;
const BILD_OK = new RegExp(`^(?:cid:|${DATEI_PFAD})${ID}$`);

function reinigen(el: Element): void {
  for (const kind of Array.from(el.children)) {
    if (ENTFERNEN.has(kind.tagName)) {
      kind.remove();
      continue;
    }
    reinigen(kind);
    if (!ERLAUBT.has(kind.tagName)) {
      kind.replaceWith(...Array.from(kind.childNodes));
      continue;
    }
    const tag = kind.tagName;
    const behalten: Record<string, string> = {};
    for (const attr of Array.from(kind.attributes)) {
      const wert = attr.value.trim();
      if (tag === "A" && attr.name === "href" && /^(https?:|mailto:|tel:)/i.test(wert)) behalten.href = wert;
      else if (tag === "IMG" && attr.name === "src" && BILD_OK.test(wert)) behalten.src = wert;
      else if (tag === "IMG" && (attr.name === "width" || attr.name === "height") && /^\d{1,4}$/.test(wert)) behalten[attr.name] = wert;
      else if (tag === "SPAN" && attr.name === "style" && FARBE.test(wert)) behalten.style = `color:${wert.replace(/^\s*color\s*:\s*/i, "").replace(/;\s*$/, "").replace(/\s+/g, "")}`;
      else if (attr.name === "class" && KLASSEN[tag]) {
        const ok = KLASSEN[tag].filter((c) => wert.split(/\s+/).includes(c));
        if (ok.length) behalten.class = ok.join(" ");
      }
    }
    if (tag === "IMG" && !behalten.src) {
      kind.remove();
      continue;
    }
    for (const attr of Array.from(kind.attributes)) kind.removeAttribute(attr.name);
    for (const [name, wert] of Object.entries(behalten)) kind.setAttribute(name, wert);
    if (tag === "IMG") kind.setAttribute("alt", "");
    if (tag === "A") {
      kind.setAttribute("target", "_blank");
      kind.setAttribute("rel", "noopener noreferrer");
    }
  }
}

/** HTML auf die erlaubte Teilmenge kürzen; Bilder bleiben nur, wenn sie auf Dateien des CRM zeigen. */
export function bereinigen(html: string | null | undefined): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(`<!doctype html><body><div>${html}</div></body>`, "text/html");
  const wurzel = doc.body.firstElementChild;
  if (!wurzel) return "";
  reinigen(wurzel);
  return wurzel.innerHTML;
}

/** Für die Anzeige (Feed, Vorschau): bereinigt, `cid:`-Bilder des CRM mit Adresse. */
export function htmlAnzeige(html: string | null | undefined): string {
  return bereinigen(cidZuUrl(html ?? ""));
}

// --- Signatur und Zitat im Text ------------------------------------------------------------------------------------------

function wurzel(html: string): { doc: Document; el: Element } {
  const doc = new DOMParser().parseFromString(`<!doctype html><body><div id="w">${html}</div></body>`, "text/html");
  return { doc, el: doc.getElementById("w") as Element };
}

function oberste(el: Element, wahl: string): Element | null {
  return Array.from(el.children).find((k) => k.matches(wahl)) ?? null;
}

export function hatSignatur(html: string): boolean {
  return /class="[^"]*crm-signatur/.test(html);
}

export function hatZitat(html: string): boolean {
  return /class="[^"]*crm-zitat/.test(html);
}

export function signaturEntfernen(html: string): string {
  const { el } = wurzel(html);
  for (const s of Array.from(el.querySelectorAll(".crm-signatur"))) s.remove();
  return el.innerHTML;
}

/** Signatur einsetzen: über dem Zitat, sonst am Ende. Eine vorhandene wird ersetzt. */
export function signaturEinsetzen(html: string, signaturHtml: string): string {
  if (!signaturHtml.trim()) return html;
  const { doc, el } = wurzel(signaturEntfernen(html));
  const block = doc.createElement("div");
  block.className = "crm-signatur";
  block.innerHTML = signaturHtml;
  const zitat = oberste(el, ".crm-zitat");
  const luft = doc.createElement("div");
  luft.innerHTML = "<br>";
  if (zitat) {
    el.insertBefore(block, zitat);
    el.insertBefore(luft, zitat);
  } else {
    if (el.innerHTML.trim()) el.appendChild(luft);
    el.appendChild(block);
  }
  return el.innerHTML;
}

/** Der Text oberhalb von Signatur und Zitat, getrennt vom Rest (zum Ersetzen durch eine Vorlage). */
export function textTeilen(html: string): { oben: string; rest: string } {
  const { el } = wurzel(html);
  const start = oberste(el, ".crm-signatur, .crm-zitat");
  if (!start) return { oben: html, rest: "" };
  const kinder = Array.from(el.children);
  const pos = kinder.indexOf(start);
  const oben = kinder.slice(0, pos).map((k) => k.outerHTML).join("");
  const rest = kinder.slice(pos).map((k) => k.outerHTML).join("");
  return { oben, rest };
}

export function istLeerText(html: string): boolean {
  const { el } = wurzel(html);
  const ohneSignatur = el.cloneNode(true) as Element;
  for (const s of Array.from(ohneSignatur.querySelectorAll(".crm-signatur, .crm-zitat"))) s.remove();
  return !(ohneSignatur.textContent ?? "").replace(/ /g, " ").trim() && !ohneSignatur.querySelector("img");
}

/** Mit Zitat/Signatur: gibt es überhaupt etwas Eigenes? */
export function istLeer(html: string): boolean {
  const { el } = wurzel(html);
  return !(el.textContent ?? "").replace(/ /g, " ").trim() && !el.querySelector("img");
}

/**
 * Anzeige einer Mail: den Teil mit früheren Nachrichten (Zitat) vom neuen Text trennen. Zitat ist ein Block mit der Klasse
 * `crm-zitat` (Antworten aus dem CRM) oder ein Zitat-Block am Ende (`blockquote`, so schreiben die meisten Mailprogramme), samt
 * der Zeile davor, wenn sie nach "… schrieb:" aussieht. Gibt es keinen neuen Text davor, wird nichts getrennt.
 */
export function textTeilenZitat(sauber: string): { haupt: string; zitat: string } {
  const { el } = wurzel(sauber);
  const kinder = Array.from(el.children);
  let start = kinder.findIndex((k) => k.matches(".crm-zitat, blockquote"));
  if (start < 0) return { haupt: sauber, zitat: "" };
  if (start > 0 && /(schrieb|wrote|geschrieben)[^:]{0,80}:\s*$/i.test((kinder[start - 1].textContent ?? "").trim())) start -= 1;
  const haupt = kinder.slice(0, start).map((k) => k.outerHTML).join("");
  const text = kinder.slice(0, start).map((k) => k.textContent ?? "").join("").replace(/ /g, " ").trim();
  // Text, der nicht in einem Element steht (nur Textknoten vor dem ersten Block), gehört zum neuen Text
  const lose = Array.from(el.childNodes).filter((n) => n.nodeType === Node.TEXT_NODE).map((n) => n.textContent ?? "").join("").trim();
  if (!text && !lose && !el.querySelector("img")) return { haupt: sauber, zitat: "" };
  const zitat = kinder.slice(start).map((k) => k.outerHTML).join("");
  return { haupt: haupt || el.firstChild?.textContent || "", zitat };
}
